import { PARTNER_ORGANIZATION, PRODUCT_CATEGORY, PRODUCT_TYPES, SUPPORTED_UNITS } from '@schuettflix/interfaces';

type ProductConfiguration = {
    id?: string;
    productType: PRODUCT_TYPES;
    category: PRODUCT_CATEGORY;
    productName: string;
    unit: SUPPORTED_UNITS;
    quantity: number;
    salesQuantity?: number;
    salesUnit?: SUPPORTED_UNITS;
    purchaseQuantity?: number;
    purchaseUnit?: SUPPORTED_UNITS;
    purchasePrice: number;
    purchaseTaxClassId: string;
    salesPrice: number;
    salesTaxClassId: string;
    partnerOrganizationId: PARTNER_ORGANIZATION;
    numberOfTransports?: number;
};

export type LineItem = {
    debtorOrganizationId: PARTNER_ORGANIZATION;
    creditorOrganizationId: PARTNER_ORGANIZATION;
    productType: PRODUCT_TYPES;
    amountNet: number;
    unitPriceAmount: number;
    productName: string;
    quantity: number;
    taxClassId: string;
    unit: SUPPORTED_UNITS;
    productConfigurationId?: string;
};

export type GenerateLineItemsParams = {
    customerOrganizationId: number;
    platformOrganizationId: number;
    productConfigurations: ProductConfiguration[];
};

export function generateLineItemsByProductConfigurations({
    customerOrganizationId,
    productConfigurations,
    platformOrganizationId,
}: GenerateLineItemsParams): LineItem[] {
    return productConfigurations.flatMap(
        ({
            productType,
            category,
            productName,
            salesPrice,
            salesTaxClassId,
            purchasePrice,
            purchaseTaxClassId,
            quantity,
            unit,
            salesQuantity,
            salesUnit,
            purchaseQuantity,
            purchaseUnit,
            id,
            partnerOrganizationId,
            numberOfTransports,
        }) => {
            const isTransport = unit === 'TRANSPORT' && category === 'TRANSPORT' && numberOfTransports;

            const resolvedSalesQuantity = salesQuantity ?? (isTransport ? numberOfTransports : quantity);
            const resolvedPurchaseQuantity = purchaseQuantity ?? (isTransport ? numberOfTransports : quantity);
            const resolvedSalesUnit = salesUnit ?? unit;
            const resolvedPurchaseUnit = purchaseUnit ?? unit;

            const isNegativeSalesPrice = salesPrice < 0;
            const isNegativePurchasePrice = purchasePrice < 0;
            return [
                {
                    ...(isNegativeSalesPrice
                        ? {
                              debtorOrganizationId: platformOrganizationId,
                              creditorOrganizationId: customerOrganizationId,
                          }
                        : {
                              debtorOrganizationId: customerOrganizationId,
                              creditorOrganizationId: platformOrganizationId,
                          }),
                    unitPriceAmount: Math.abs(salesPrice),
                    amountNet: Math.abs(salesPrice * resolvedSalesQuantity),
                    quantity: resolvedSalesQuantity,
                    taxClassId: salesTaxClassId,
                    productType,
                    productName,
                    unit: resolvedSalesUnit,
                    productConfigurationId: id,
                },
                {
                    ...(isNegativePurchasePrice
                        ? {
                              debtorOrganizationId: partnerOrganizationId,
                              creditorOrganizationId: platformOrganizationId,
                          }
                        : {
                              debtorOrganizationId: platformOrganizationId,
                              creditorOrganizationId: partnerOrganizationId,
                          }),
                    unitPriceAmount: Math.abs(purchasePrice),
                    amountNet: Math.abs(purchasePrice * resolvedPurchaseQuantity),
                    quantity: resolvedPurchaseQuantity,
                    taxClassId: purchaseTaxClassId,
                    productType,
                    productName,
                    unit: resolvedPurchaseUnit,
                    productConfigurationId: id,
                },
            ];
        }
    );
}
